import { Button } from '@cloudflare/kumo';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createRoute, Link, useParams } from '@tanstack/react-router';
import { ownerLabel } from '../../shared/contracts.ts';
import { api, queryClient, unwrap } from '../api.ts';
import { ErrorNotice, Loading } from '../common.tsx';
import { Route as parent } from './signed-in.tsx';
export const Route = createRoute({
  getParentRoute: () => parent,
  path: '/invitations/$kind/$invitationId',
  component: InvitationPage,
});

function InvitationPage() {
  const { invitationId, kind } = useParams({ strict: false });
  const siteInvitation = kind === 'site';
  const invitation = useQuery({
    queryKey: ['invitation', kind, invitationId],
    queryFn: async () => {
      if (kind !== 'site' && kind !== 'billing') throw new Error('Unknown invitation kind.');
      return siteInvitation
        ? unwrap(
            await api.GET('/api/site-invitations/{invitationId}', {
              params: { path: { invitationId: invitationId! } },
            }),
          )
        : unwrap(
            await api.GET('/api/billing-invitations/{invitationId}', {
              params: { path: { invitationId: invitationId! } },
            }),
          );
    },
  });
  const answer = useMutation({
    mutationFn: async (accept: boolean) => {
      const params = { path: { invitationId: invitationId! } };
      if (siteInvitation)
        return accept
          ? unwrap(await api.POST('/api/site-invitations/{invitationId}/accept', { params, body: {} }))
          : unwrap(await api.POST('/api/site-invitations/{invitationId}/decline', { params, body: {} }));
      return accept
        ? unwrap(await api.POST('/api/billing-invitations/{invitationId}/accept', { params, body: {} }))
        : unwrap(await api.POST('/api/billing-invitations/{invitationId}/decline', { params, body: {} }));
    },
    onSuccess: () => queryClient.invalidateQueries(),
  });
  if (invitation.isPending) return <Loading />;
  if (invitation.error)
    return (
      <main className="mx-auto my-12 p-6 max-w-168 [&_p]:mx-0 [&_p]:my-4 [&_p]:leading-[1.65] [&_.checkbox-row]:mx-0 [&_.checkbox-row]:my-5 [&_.action-row]:mt-6 [&_.action-row]:flex-wrap">
        <h1 className="text-[30px] leading-[1.2] font-semibold tracking-[-0.8px]">Invitation unavailable</h1>
        <ErrorNotice error={invitation.error} />
        <p className="leading-[1.65]">
          Sign in with the email address that received this invitation. If you’re using another address, sign
          out first.
        </p>
      </main>
    );
  const info = invitation.data;
  return (
    <main className="mx-auto my-12 p-6 max-w-168 [&_p]:mx-0 [&_p]:my-4 [&_p]:leading-[1.65] [&_.checkbox-row]:mx-0 [&_.checkbox-row]:my-5 [&_.action-row]:mt-6 [&_.action-row]:flex-wrap">
      <p className="mx-0 mt-0 mb-3 leading-[1.65] text-[11px] font-semibold text-kumo-subtle uppercase tracking-[1.5px]">
        {ownerLabel({ name: info.ownerName, email: info.ownerEmail })}
      </p>
      <h1 className="text-[30px] leading-[1.2] font-semibold tracking-[-0.8px]">
        {info.kind === 'Site' ? `Edit ${info.siteName}` : 'Payment-method access'}
      </h1>
      <p className="leading-[1.65]">
        {info.kind === 'Site'
          ? 'You can edit and publish this site. Billing, sharing, and ownership stay with the owner.'
          : `You can view invoices and update the payment method for ${ownerLabel({ name: info.ownerName, email: info.ownerEmail })}. Changing the payment method affects the entire subscription. You won’t have access to website drafts or form entries.`}
      </p>
      <ErrorNotice error={answer.error} />
      {info.status === 'pending' ? (
        <div className="action-row flex gap-2 items-center">
          <Button variant="primary" disabled={answer.isPending} onClick={() => answer.mutate(true)}>
            Accept invitation
          </Button>
          <Button disabled={answer.isPending} variant="ghost" onClick={() => answer.mutate(false)}>
            Decline
          </Button>
        </div>
      ) : info.status === 'accepted' ? (
        <>
          <p className="leading-[1.65]" role="status">
            You have access.
          </p>
          {info.kind === 'Site' && info.siteId ? (
            <Link to="/sites/$siteId/pages" params={{ siteId: info.siteId }}>
              Open website
            </Link>
          ) : (
            <Link to="/billing" search={{ account: info.accountId }}>
              Open payment methods
            </Link>
          )}
        </>
      ) : (
        <p className="leading-[1.65]">
          This invitation is {info.status}. Ask the account owner for a new one if you need access.
        </p>
      )}
    </main>
  );
}
